import deleteIcon from 'assets/icons/deleteIcon.svg';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import classes from './UploadExcelValidation.module.scss';
import {
  formatNDC11,
  formatPeriodHeader,
  formatPlanningPeriod,
  useUploadExcelValidation,
} from './UploadExcelValidation.logic';
import type { ValidationGridRow } from './UploadExcelValidation.type';

const UploadExcelValidation = observer(({ onClose }: { onClose: () => void }) => {
  const {
    gridRows,
    hasBlockingErrors,
    summary,
    planningPeriod,
    isCreating,
    handleNoteChange,
    handleDeleteRow,
    handleCreate,
    handleClose,
  } = useUploadExcelValidation(onClose);

  const periodColumns: string[] =
    gridRows.length > 0 ? gridRows[0].forecast_data.map(fd => fd.forecast_period) : [];

  const footer = (
    <div className={classes.footer}>
      <span className={classes.footerSummary}>
        {summary.total} row{summary.total !== 1 ? 's' : ''}
        {summary.errors > 0 ? (
          <>
            {' — '}
            <span className={classes.errorCount}>{summary.errors} error(s)</span>
          </>
        ) : (
          <>
            {' — '}
            <span className={classes.allValid}>all valid</span>
          </>
        )}
      </span>
      <div className={classes.footerButtons}>
        <Button label="Cancel" className="p-button-outlined" onClick={handleClose} />
        <Button
          label="Create"
          loading={isCreating}
          disabled={hasBlockingErrors || gridRows.length === 0}
          onClick={handleCreate}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      blockScroll
      resizable={false}
      draggable={false}
      header={`Upload Forecast — Planning Period: ${formatPlanningPeriod(planningPeriod)}`}
      visible
      style={{ width: '95vw' }}
      contentStyle={{ padding: '0.5rem 0 0 0' }}
      footer={footer}
      onHide={handleClose}
    >
      <DataTable
        value={gridRows}
        scrollable
        scrollHeight="calc(90vh - 200px)"
        size="small"
        className={classes.table}
        tableStyle={{ minWidth: '2270px' }}
        rowClassName={(row: ValidationGridRow) =>
          row.validation.status === 'error' ? classes.errorRow : ''
        }
      >
        <Column
          header="#"
          body={(_row: ValidationGridRow, opts) => opts.rowIndex + 1}
          style={{ width: '50px', minWidth: '50px' }}
        />
        <Column
          field="concept"
          header="Concept"
          style={{ width: '150px', minWidth: '150px' }}
        />
        <Column
          header="NDC"
          style={{ width: '140px', minWidth: '140px' }}
          body={(row: ValidationGridRow) => formatNDC11(row.ndc11)}
        />
        <Column
          header="Contract Type"
          style={{ width: '120px', minWidth: '120px' }}
          body={(row: ValidationGridRow) => (
            <span className={classes.contractChip}>{row.contract_type}</span>
          )}
        />
        <Column
          field="status"
          header="Status"
          style={{ width: '90px', minWidth: '90px' }}
        />

        {periodColumns.map(period => (
          <Column
            key={period}
            header={formatPeriodHeader(period)}
            style={{ width: '80px', minWidth: '80px' }}
            body={(row: ValidationGridRow) => {
              const entry = row.forecast_data.find(fd => fd.forecast_period === period);
              return (
                <span className={classes.monthValue}>
                  {(entry?.value ?? 0).toLocaleString('en-US')}
                </span>
              );
            }}
          />
        ))}

        <Column
          header="Validation"
          style={{ width: '220px', minWidth: '220px' }}
          body={(row: ValidationGridRow) => {
            if (row.validation.status === 'ok') {
              return <span className={classes.chipOk}>OK</span>;
            }
            return (
              <div className={classes.validationErrorCell}>
                <span className={classes.chipError}>Error</span>
                <ul className={classes.validationMessageList}>
                  {row.validation.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            );
          }}
        />

        <Column
          header="Notes"
          style={{ width: '220px', minWidth: '220px' }}
          body={(row: ValidationGridRow, opts) => (
            <InputText
              value={row.note}
              onChange={e => handleNoteChange(opts.rowIndex, e.target.value)}
              placeholder="Add note…"
              maxLength={255}
              className={classes.noteInput}
            />
          )}
        />

        <Column
          header="Action"
          style={{ width: '80px', minWidth: '80px' }}
          body={(_row: ValidationGridRow, opts) => (
            <div className={classes.actionCell}>
              <img
                src={deleteIcon}
                alt="Delete row"
                className={classes.deleteRowIcon}
                onClick={() => handleDeleteRow(opts.rowIndex)}
              />
            </div>
          )}
        />
      </DataTable>
    </Dialog>
  );
});

export default UploadExcelValidation;
